import React, { useCallback, useRef } from 'react';
import { apolloClient, gql } from 'lib/graphql';
import SearchInput, { Item } from '../SearchInput';

export type Skill = Item;

interface SkillsSearchInputProps {
  context?: 'talent' | 'company' | 'neutral';
  initialSkills: Skill[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  persist?: boolean;
  error?: string;
  onChange?: (skills: Skill[]) => void;
}

const GQL_QUERY_TAGS = gql`
  query ($query: String) {
    tags(query: $query, excludeUserTags: true) {
      nodes {
        id
        name
      }
    }
  }
`;

const GQL_ADD_SKILL = gql`
  mutation ($tagId: ID!) {
    candidate {
      skillCreate(tagId: $tagId) {
        errors {
          field
          message
        }
        resource {
          id
          tag {
            name
          }
        }
      }
    }
  }
`;

const GQL_DELETE_SKILL = gql`
  mutation ($id: ID!) {
    candidate {
      skillDelete(id: $id) {
        errors {
          field
          message
        }
      }
    }
  }
`;

export default function SkillsSearchInput({
  context,
  initialSkills,
  label = 'Skills',
  placeholder = 'e.g. HTML',
  required = false,
  persist = false,
  error,
  onChange,
}: SkillsSearchInputProps) {
  const skillsRef = useRef<Skill[]>(initialSkills);

  const loadOptions = useCallback(async (inputValue: string) => {
    if (!inputValue.trim()) return [];

    const result = await apolloClient.query({
      query: GQL_QUERY_TAGS,
      variables: { query: inputValue },
    });

    return result.data.tags.nodes
      .filter((t: any) => !skillsRef.current.some(s => s.value === t.id))
      .map((t: any) => ({ value: t.id, label: t.name }));
  }, []);

  const handleChange = async (updatedSkills: Skill[]) => {
    const previous = skillsRef.current;
    skillsRef.current = updatedSkills;
    onChange?.(updatedSkills);

    if (!persist) return;

    const added = updatedSkills.find(s => !previous.some(p => p.value === s.value));
    const removed = previous.find(s => !updatedSkills.some(u => u.value === s.value));

    if (added) {
      try {
        const result = await apolloClient.mutate({
          mutation: GQL_ADD_SKILL,
          variables: { tagId: added.value },
        });
        const created = result.data?.candidate?.skillCreate?.resource;
        if (created) {
          skillsRef.current = skillsRef.current.map(s =>
            s.value === added.value ? { value: created.id, label: created.tag.name } : s
          );
          onChange?.(skillsRef.current);
        }
      } catch {
        skillsRef.current = skillsRef.current.filter(s => s.value !== added.value);
        onChange?.(skillsRef.current);
      }
    }

    if (removed) {
      try {
        await apolloClient.mutate({
          mutation: GQL_DELETE_SKILL,
          variables: { id: removed.value },
        });
      } catch {
        skillsRef.current = [...skillsRef.current, removed];
        onChange?.(skillsRef.current);
      }
    }
  };

  return (
    <SearchInput
      context={context}
      initialItems={initialSkills}
      loadOptions={loadOptions}
      label={label}
      placeholder={placeholder}
      required={required}
      error={error}
      onChange={handleChange}
    />
  );
}
